import { trackEvent, TrackedEvent } from "@/domains/metrics";
import { useTimedFlag } from "@/domains/react/use-timed-flag";
import { CustomEventName, useCustomEvent } from "@/modules/custom-event";
import { AudioRecordingObservable } from "@/store/audio-recordings";
import { Duration } from "luxon";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

export const useVoiceIndicatorBehavior = ({
  noteId,
  audioRecording,
  isSuggestionActive,
}: {
  noteId: string;
  audioRecording?: AudioRecordingObservable | null;
  isSuggestionActive: boolean;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const { activate: suppressOutsideClose, isActive: isOutsideCloseSuppressed } = useTimedFlag({
    duration: Duration.fromObject({ milliseconds: 250 }),
  });

  const openForSuggestion = useCallback(() => {
    suppressOutsideClose();
    setIsOpen(true);
  }, [suppressOutsideClose]);

  useCustomEvent(CustomEventName.ExpandVoicePopover, ({ noteId: eventNoteId }) => {
    if (eventNoteId === noteId) {
      setIsOpen(true);
    }
  });

  const hasTrackedSuggestionRef = useRef<string | null>(null);

  useEffect(() => {
    const audioRecordingId = audioRecording?.id;
    if (!audioRecordingId) return;
    if (!isSuggestionActive) return;
    if (hasTrackedSuggestionRef.current === audioRecordingId) return;

    hasTrackedSuggestionRef.current = audioRecordingId;
    trackEvent(TrackedEvent.VoiceSessionSuggestedEditsView);

    // Auto-open the voice popover when suggestion arrives
    openForSuggestion();
  }, [isSuggestionActive, audioRecording, audioRecording?.id, openForSuggestion]);

  useLayoutEffect(() => {
    if (isSuggestionActive) {
      openForSuggestion();
    }
  }, [isSuggestionActive, openForSuggestion]);

  const ref = useRef<HTMLButtonElement>(null);
  const popoverContentRef = useRef<HTMLDivElement>(null);

  const handleRequestClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    // Use requestAnimationFrame to ensure the popover is rendered before focusing
    requestAnimationFrame(() => {
      if (popoverContentRef.current) {
        // Focus the popover content to enable outside click detection
        popoverContentRef.current.focus();
      }
    });
  }, [isOpen]);

  return {
    handleRequestClose,
    isOutsideCloseSuppressed,
    popoverContentRef,
    ref,
    setIsOpen,
    isOpen,
  };
};
